import React, { useMemo, useState } from 'react';
import { C } from '../../../components/UI';
import { MarketplaceAppCatalogDto } from '../../../services/api';
import AppCard from './AppCard';

export default function AppBrowseView({ apps, onSelect }: { apps: MarketplaceAppCatalogDto[]; onSelect: (appId: string) => void }) {
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState<string>('All');

    const categories = useMemo(() => ['All', ...Array.from(new Set(apps.map(a => a.category))).sort()], [apps]);
    const featured = useMemo(() => apps.filter(a => a.isFeatured), [apps]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return apps.filter(a =>
            (category === 'All' || a.category === category) &&
            (!q || a.name.toLowerCase().includes(q) || a.shortDescription.toLowerCase().includes(q))
        );
    }, [apps, search, category]);

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: C.text }}>App Marketplace</h2>
                <p style={{ margin: 0, fontSize: 13.5, color: C.textSub }}>Connect tools and add-ons to extend what your account can do.</p>
            </div>

            <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search app names or features"
                style={{
                    width: '100%', maxWidth: 480, padding: '10px 14px', marginBottom: 28,
                    border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none',
                    fontFamily: "'Inter', sans-serif",
                }}
            />

            {!search && category === 'All' && featured.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textSub, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Featured
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
                        {featured.map(a => <AppCard key={a.id} app={a} onClick={() => onSelect(a.id)} />)}
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
                <div style={{ width: 170, flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textSub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Categories
                    </div>
                    {categories.map(cat => (
                        <div
                            key={cat}
                            onClick={() => setCategory(cat)}
                            style={{
                                padding: '7px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13.5,
                                fontWeight: category === cat ? 700 : 400,
                                color: category === cat ? 'var(--color-button, #1D9E75)' : C.textSub,
                                background: category === cat ? C.greenBg : 'transparent',
                                marginBottom: 2,
                            }}
                        >
                            {cat}
                        </div>
                    ))}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textSub, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        All apps
                    </div>
                    {filtered.length === 0 ? (
                        <div style={{ color: C.textMuted, fontSize: 13.5, padding: '20px 0' }}>No apps match your search.</div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
                            {filtered.map(a => <AppCard key={a.id} app={a} onClick={() => onSelect(a.id)} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
